import { lazy, Suspense, useEffect, useState, type JSX, type ComponentType } from 'react'
import { Smile } from 'lucide-react'

type EmojiMartPickerProps = { data: unknown; onEmojiSelect: (e: { native: string }) => void }

const EmojiMartPicker = lazy(() =>
  import('@emoji-mart/react').then((m) => ({ default: m.default as ComponentType<EmojiMartPickerProps> })),
)

function EmojiPickerWithData({ onSelect }: { onSelect: (emoji: string) => void }): JSX.Element {
  const [data, setData] = useState<unknown>(null)

  useEffect(() => {
    void import('@emoji-mart/data').then((m) => setData(m.default))
  }, [])

  if (!data) {
    return <div className="h-80 w-80 animate-pulse rounded-lg bg-gray-100" />
  }

  return (
    <Suspense fallback={<div className="h-80 w-80 animate-pulse rounded-lg bg-gray-100" />}>
      <EmojiMartPicker data={data} onEmojiSelect={(e) => onSelect(e.native)} />
    </Suspense>
  )
}

export interface EmojiPickerProps {
  onSelect: (emoji: string) => void
}

export default function EmojiPicker({ onSelect }: EmojiPickerProps): JSX.Element {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Insert emoji"
        title="Insert emoji"
        className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 active:scale-95"
      >
        <Smile className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-50 mb-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          <EmojiPickerWithData
            onSelect={(emoji) => {
              onSelect(emoji)
              setOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
